import { useState } from "react";
import { useClick, useLosers, useStats, useWinners } from "./hooks/useApi";

function App() {
  const [hasWon, setHasWon] = useState(false);
  const [hasLost, setHasLost] = useState(false);
  const [winnerIP, setWinnerIP] = useState<string>("");
  const [winnerKey, setWinnerKey] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // TanStack Query hooks
  const { data: winners = [], isLoading: winnersLoading } = useWinners();
  const { data: losers = [] } = useLosers();
  const { data: stats } = useStats();
  const clickMutation = useClick();

  const totalAttempts = stats?.totalAttempts || 0;

  const handleClick = async () => {
    setErrorMessage("");

    try {
      const result = await clickMutation.mutateAsync();

      if (result.success) {
        if (result.won) {
          setWinnerIP(result.ip);
          setWinnerKey(result.winnerKey || "");
          setHasWon(true);
          console.log("🎉 WINNER! IP Address:", result.ip);
          console.log("🎉 Winner Key:", result.winnerKey);
        } else {
          // User lost, show the loser modal
          setHasLost(true);
        }
      } else {
        // Handle rate limiting or other errors
        setErrorMessage(result.message || result.error || "An error occurred");
      }
    } catch (error) {
      console.error("Failed to process click:", error);
      setErrorMessage("Network error occurred");
    }
  };

  const resetGame = () => {
    setHasWon(false);
    setHasLost(false);
    setWinnerIP("");
    setWinnerKey("");
    setErrorMessage("");
  };

  const getRandomFunnySaying = () => {
    const sayings = [
      "Lady Luck must be taking a coffee break ☕",
      "The universe has a sense of humor... and it's not funny 😅",
      "Better luck next time, statistician! 📊",
      "Somewhere out there, a probability professor is laughing 🤓",
      "Your luck is rarer than a unicorn riding a rainbow 🦄",
      "The odds were not in your favor, Katniss 🏹",
      "Murphy's Law strikes again! 🔧",
      "Fortune favors the bold... but not today 🛡️",
      "Randomness: 1, You: 0 🎲",
      "The RNG gods are feeling spicy today 🌶️",
      "You just experienced a statistical miracle... of bad luck 📉",
      "Congratulations! You're part of the 99.9999999% club 🎯",
      "Your luck is so rare, it should be in a museum 🏛️",
      "Breaking: Local person discovers laws of probability still apply 📰",
      "Plot twist: The house always wins 🏠",
      "You've been blessed by the curse of normal distribution 📊",
      "Unlucky level: Expert mode activated 🎮",
      "Your misfortune is mathematically impressive 🧮",
      "The cosmic dice rolled snake eyes 🐍",
      "Luck.exe has stopped working 💻"
    ];
    return sayings[Math.floor(Math.random() * sayings.length)];
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Winner Modal Overlay */}
      {hasWon && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 px-4">
          <div className="text-center max-w-2xl">
            <div className="mb-8">
              <h1 className="text-6xl font-bold text-white mb-4">
                🎉 WINNER! 🎉
              </h1>
              <p className="text-xl text-white mb-2">
                Congratulations! You beat the odds.
              </p>
              <p className="text-lg text-white mb-2">
                Total attempts: {totalAttempts.toLocaleString()}
              </p>
              {winnerIP && (
                <p className="text-sm text-white opacity-75 mb-4">
                  Winner IP: {winnerIP}
                </p>
              )}

              {winnerKey && (
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-lg mb-6 text-black">
                  <h3 className="text-xl font-bold mb-2">
                    🎉 Your Winner Key 🎉
                  </h3>
                  <div className="bg-white p-4 rounded font-mono text-lg text-center break-all">
                    {winnerKey}
                  </div>
                  <p className="text-sm mt-2 font-medium">
                    This proves you beat 1-in-1-billion odds! Screenshot this!
                  </p>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(winnerKey)}
                    className="mt-3 bg-black text-white px-4 py-2 rounded text-sm hover:bg-gray-800 transition-colors"
                  >
                    Copy Key
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={resetGame}
              className="bg-white hover:bg-gray-200 text-black font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Continue Playing
            </button>
          </div>
        </div>
      )}

      {/* Loser Modal Overlay */}
      {hasLost && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 px-4">
          <div className="text-center max-w-2xl">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-red-400 mb-6">
                💀 Unlucky! 💀
              </h1>
              <div className="bg-gradient-to-r from-red-500 to-pink-500 p-6 rounded-lg mb-6 text-white">
                <p className="text-xl mb-4 font-medium">
                  {getRandomFunnySaying()}
                </p>
                <div className="bg-black bg-opacity-30 p-4 rounded-lg">
                  <p className="text-lg mb-2">
                    <span className="font-bold">Total attempts:</span> {totalAttempts.toLocaleString()}
                  </p>
                  <p className="text-lg">
                    <span className="font-bold">Your odds were:</span> 1 in 1,000,000,000
                  </p>
                  <p className="text-sm mt-2 opacity-75">
                    (That's 0.0000001% chance of winning)
                  </p>
                </div>
                <p className="text-sm mt-4 font-medium">
                  📸 Screenshot this epic fail and share your unlucky moment!
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={resetGame}
              className="bg-white hover:bg-gray-200 text-black font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Main game area */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="text-center max-w-2xl">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4 tracking-wide">
            1 Billion in 1
          </h1>

          <div className="h-14">
            {totalAttempts > 0 && (
              <p className="text-2xl md:text-3xl text-gray-300 font-light mb-8">
                {totalAttempts.toLocaleString()} attempts
              </p>
            )}
          </div>

          <div className="mb-10 space-y-3">
            <p className="text-lg md:text-xl text-gray-300 font-light">
              Click the button for a 1 in 1,000,000,000 chance to win
            </p>
            <p className="text-sm text-gray-500">
              0.0000001% probability of success
            </p>
            <div className="text-red-400 text-sm font-medium h-2">
              {errorMessage && <p>{errorMessage}</p>}
            </div>
          </div>

          <button
            type="button"
            onClick={handleClick}
            disabled={clickMutation.isPending}
            className="bg-white hover:bg-gray-100 disabled:bg-gray-400 disabled:cursor-not-allowed text-black font-medium py-4 px-16 rounded-full text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {clickMutation.isPending ? "Processing..." : "Try Your Luck"}
          </button>
        </div>
      </div>

      {/* Tables section */}
      <div className="px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-[32rem] md:h-96">
            {/* Winner Clicks Table - Mobile: First */}
            <div className="order-1 md:order-none bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-800">
                <h2 className="text-lg font-medium text-white">
                  Winners ({winnersLoading ? "..." : winners.length})
                </h2>
              </div>
              <div className="h-[28rem] md:h-80 overflow-y-auto">
                {winnersLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 text-sm">Loading winners...</p>
                  </div>
                ) : winners.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 text-sm">No winners yet...</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-950 border-b border-gray-800">
                      <tr>
                        <th className="text-left text-gray-300 py-3 px-4 font-medium">
                          IP Address
                        </th>
                        <th className="text-left text-gray-300 py-3 px-4 font-medium">
                          Winner Key
                        </th>
                        <th className="text-left text-gray-300 py-3 px-4 font-medium">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {winners.map((click) => (
                        <tr
                          key={click.id}
                          className="border-b border-gray-900 hover:bg-gray-900/50"
                        >
                          <td className="text-white py-3 px-4 font-mono text-xs">
                            {click.ip}
                          </td>
                          <td className="text-yellow-400 py-3 px-4 font-mono text-xs">
                            {click.winnerKey || "N/A"}
                          </td>
                          <td className="text-gray-300 py-3 px-4 text-xs">
                            {new Date(click.timestamp).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Loser Clicks Table - Mobile: Second */}
            <div className="order-2 md:order-none bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-800">
                <h2 className="text-lg font-medium text-white">
                  Recent Attempts ({losers.length})
                </h2>
              </div>
              <div className="h-[28rem] md:h-80 overflow-y-auto">
                {losers.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 text-sm">No attempts yet...</p>
                  </div>
                ) : (
                  <div>
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-950 border-b border-gray-800">
                        <tr>
                          <th className="text-left text-gray-300 py-3 px-4 font-medium">
                            IP Address
                          </th>
                          <th className="text-left text-gray-300 py-3 px-4 font-medium">
                            Time
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {losers.slice(0, 50).map((click) => (
                          <tr
                            key={click.id}
                            className="border-b border-gray-900 hover:bg-gray-900/50"
                          >
                            <td className="text-white py-3 px-4 font-mono text-xs">
                              {click.ip}
                            </td>
                            <td className="text-gray-300 py-3 px-4 text-xs">
                              {new Date(click.timestamp).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {losers.length > 50 && (
                      <div className="sticky bottom-0 bg-gray-950 py-2 text-center border-t border-gray-800">
                        <p className="text-gray-500 text-xs">
                          Showing last 50 attempts
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
